<template lang="pug">
.notifications.profile-content
  p(
    v-if="state === 'error'"
  ) {{$options.text.error}}

  .notifications__no-data(
    v-else-if="state === 'no-data'"
  )
    BannerNoData(
      :title="noData.title"
      :text="noData.text"
    )

  .notifications__list(v-else)
    Notification(
      v-for="notification of items"
      :key="notification._id"
      :link="notification.redirect_link || notification.path"
      :userName="getNotificationContent(notification, true).name"
      :userAvatar="getPhotoStyle(notification, true)"
      :notificationDate="getHumanReadableDate(notification.event_at)"
      :notificationContent="getNotificationContent(notification, true).content"
      :isRead="Boolean(notification.is_read)"
    )
    ProfileCardPlaceholder(v-if="state === 'fetching'")
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { getAccessToken } from '@devhacker/shared/features/auth/utils/token';
  import Notification from '~/components/Notification/Notification.vue';
  import {
    getPhotoStyle,
    getNotificationContent,
    getHumanReadableDate,
  } from '~/components/Pusher/utils.client';

  const BannerNoData = defineAsyncComponent(
    () => import('~/components/BannerNoData/BannerNoData.vue'),
  );
  const ProfileCardPlaceholder = defineAsyncComponent(
    () => import('~/placeholders/ProfileCardPlaceholder/ProfileCardPlaceholder.vue'),
  );

  const NOTIFICATIONS_COUNT_ON_PAGE = 30;

  export default defineNuxtComponent({
    name: 'ProfileNotificationsPage',

    components: {
      BannerNoData,
      Notification,
      ProfileCardPlaceholder,
    },

    text: {
      error: 'Произошла ошибка при загрузке уведомлений.',
    },

    noData: {
      own: {
        text: 'Участвуйте в дискуссиях, и здесь что-то появится!',
      },
      default: {
        title: 'Нет уведомлений',
      },
    },

    props: {
      userId: {
        type: String as PropType<string>,
        required: true,
      },
      isOwnProfile: {
        type: Boolean as PropType<boolean>,
        default: false,
      },
    },

    async setup(props) {
      const state = ref('pending');
      const page = ref(1);
      const total = ref(0);
      const items = ref([]);
      const userId = props.userId;

      state.value = 'fetching';
      const token = getAccessToken();

      const {
        data,
        status,
        error,
        execute: makeNotificationRequest,
      } = useAsyncData(
        () =>
          useNuxtApp().$pusherApi.fetchNotifications(token, userId, {
            page: page.value,
            limit: NOTIFICATIONS_COUNT_ON_PAGE,
          }),
        { server: false, lazy: true },
      );

      onMounted(async () => {
        await makeNotificationRequest();

        if (error.value || status.value === 'error') {
          state.value = 'error';
          console.error('error pusher Request', error);
        }

        if (status.value === 'success') {
          total.value = data.value?.data.meta?.total ?? 0;
          const posts = data.value?.data.data;
          if (posts) {
            items.value.push(...posts);
          }

          const noData = total.value === 0;
          const allPostsLoaded = items.value.length === total.value;
          if (noData) {
            state.value = 'no-data';
          } else if (allPostsLoaded) {
            state.value = 'fulfilled';
          } else {
            page.value += 1;
            state.value = 'pending';
          }
        }
      });

      return {
        state,
        page,
        total,
        items,
      };
    },

    computed: {
      noData() {
        return {
          ...this.$options.noData.default,
          ...(this.isOwnProfile ? this.$options.noData.own : {}),
        };
      },
    },

    methods: {
      getNotificationContent,
      getPhotoStyle,
      getHumanReadableDate,
    },
  });
</script>

<style lang="scss" scoped>
  .notifications {
    &__no-data {
      width: 100%;
      padding: 40px 0 80px;
      text-align: center;

      @include tablet {
        padding: 30px 0 120px;
      }
    }
    :deep() {
      .banner__title {
        margin-top: 24px;
      }
    }
  }
</style>
